import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Plus, Image, Video } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { privateAlbumService } from '@/services/privateAlbumService';
import { usePrivateAlbumLimits } from '@/hooks/usePrivateAlbumLimits';
import PrivateAlbumCreationDialog from './PrivateAlbumCreationDialog';
import PrivateAlbumsList from './PrivateAlbumsList';
import { toast } from '@/hooks/use-toast';

const PrivateAlbumsSection = () => {
  const { user } = useAuth();
  const { limits, loading, refreshLimits } = usePrivateAlbumLimits();
  const [albums, setAlbums] = useState<any[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (user) {
      fetchAlbums();
    }
  }, [user, refreshKey]);

  const fetchAlbums = async () => {
    if (!user) return;
    
    try {
      const userAlbums = await privateAlbumService.getUserAlbums(user.id);
      setAlbums(userAlbums);
    } catch (error) {
      console.error('Error fetching albums:', error);
      toast({
        title: "Error",
        description: "Failed to load private albums",
        variant: "destructive"
      });
    }
  };

  const handleAlbumCreated = () => {
    setRefreshKey(prev => prev + 1);
    refreshLimits();
    setShowCreateDialog(false);
  };

  const handleAlbumDeleted = () => {
    setRefreshKey(prev => prev + 1);
    refreshLimits();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Lock className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-semibold">Private Albums</h3>
          </div>
          <div className="text-center py-8">
            <div className="animate-pulse">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-semibold">Private Albums</h3>
          </div>
          
          <div className="text-sm text-muted-foreground">
            {limits.currentAlbumCount}/{limits.maxAlbums} albums
          </div>
        </div>

        <div className="space-y-4">
          {/* Album limits info */}
          <div className="text-sm text-muted-foreground space-y-1">
            <div className="flex items-center space-x-2">
              <Image className="w-4 h-4" />
              <span>Images: Max 1028×1028px, 1MB each</span>
            </div>
            <div className="flex items-center space-x-2">
              <Video className="w-4 h-4" />
              <span>Videos: Max 30MB, 15sec each, 1 per album</span>
            </div>
          </div>

          {/* Create Album Button */}
          <div className="flex justify-center">
            <Button
              onClick={() => setShowCreateDialog(true)}
              disabled={!limits.canCreateAlbum}
              variant="outline"
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Private Album
              {!limits.canCreateAlbum && (
                <span className="ml-2 text-xs">
                  ({limits.subscriptionTier === 'basic' ? 'Upgrade for more' : 'Limit reached'})
                </span>
              )}
            </Button>
          </div>

          {/* Albums List */}
          <PrivateAlbumsList 
            albums={albums}
            refreshKey={refreshKey}
            onAlbumDeleted={handleAlbumDeleted}
            limits={limits}
          />
        </div>

        <PrivateAlbumCreationDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onAlbumCreated={handleAlbumCreated}
        />
      </CardContent>
    </Card>
  );
};

export default PrivateAlbumsSection;